import React, { FC, useRef, useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import WordcloudModule from 'highcharts/modules/wordcloud';
import { MODERN_COLORS, FONT_FAMILY } from '../../themes/highcharts-theme';

WordcloudModule(Highcharts);

export interface WordCloudWord {
  text: string;
  value: number;
}

interface Props {
  words: WordCloudWord[];
  height?: number;
  onWordClick?: (word: string) => void;
}

const interpolateColor = (hex: string, weight: number, min: number, max: number): string => {
  const t = max === min ? 1 : (weight - min) / (max - min);
  const opacity = 0.45 + t * 0.55;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${opacity})`;
};

const WordCloudChart: FC<Props> = ({ words, height = 500, onWordClick }) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const chartOptions: Highcharts.Options = useMemo(() => {
    const maxWeight = Math.max(...words.map((w) => w.value), 1);
    const minWeight = Math.min(...words.map((w) => w.value), 0);

    return {
      chart: {
        height,
        margin: [8, 8, 8, 8],
        backgroundColor: 'transparent',
      },
      title: { text: undefined },
      tooltip: {
        useHTML: true,
        formatter: function () {
          const weight = (this.point as any).weight;
          const pct = Math.round((weight / maxWeight) * 100);
          return `
            <div style="text-align:center;min-width:100px">
              <div style="font-size:14px;font-weight:600;margin-bottom:4px">${this.key}</div>
              <div style="font-size:20px;font-weight:700;color:${(this.point as any).color}">${weight.toLocaleString()}</div>
              <div style="margin-top:6px;height:4px;border-radius:2px;background:#e2e8f0;overflow:hidden">
                <div style="width:${pct}%;height:100%;border-radius:2px;background:${(this.point as any).color}"></div>
              </div>
              <div style="font-size:10px;color:#94a3b8;margin-top:4px">${pct}% of max</div>
            </div>`;
        },
      },
      plotOptions: {
        wordcloud: {
          cursor: onWordClick ? 'pointer' : 'default',
          animation: { duration: 800 },
          states: {
            hover: { brightness: 0.15 },
            inactive: { opacity: 0.35 },
          },
          events: onWordClick
            ? { click: (e: any) => onWordClick(e.point.name) }
            : undefined,
        } as any,
      },
      series: [
        {
          type: 'wordcloud' as any,
          data: words.map((w, i) => ({
            name: w.text,
            weight: w.value,
            color: interpolateColor(
              MODERN_COLORS[i % MODERN_COLORS.length],
              w.value,
              minWeight,
              maxWeight,
            ),
          })),
          minFontSize: 12,
          maxFontSize: 72,
          spiral: 'archimedean',
          placementStrategy: 'center',
          rotation: { from: 0, to: 0, orientations: 1 },
          style: { fontFamily: FONT_FAMILY, fontWeight: '600' },
        } as any,
      ],
    };
  }, [words, height, onWordClick]);

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={chartOptions}
      ref={chartRef}
    />
  );
};

export default WordCloudChart;
